import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WalletIcon, StarIcon, HistoryIcon, GiftIcon, UserIcon, MenuIcon, XIcon, LogOutIcon, HomeIcon, QrCodeIcon, SettingsIcon } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { Badge } from '../ui/Badge';
import { getTierNameVi } from '../../lib/formatters';
export function CustomerNav() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const {
    isCustomerLoggedIn,
    currentCustomer,
    customerLogout
  } = useAuth();
  const navLinks = [{
    to: '/',
    label: 'Trang Chủ',
    icon: HomeIcon
  }, {
    to: '/vi',
    label: 'Ví MHP',
    icon: WalletIcon
  }, {
    to: '/diem',
    label: 'Điểm & Hạng',
    icon: StarIcon
  }, {
    to: '/lich-su',
    label: 'Lịch Sử',
    icon: HistoryIcon
  }, {
    to: '/phan-thuong',
    label: 'Phần Thưởng',
    icon: GiftIcon
  }, {
    to: '/thanh-toan',
    label: 'Thanh Toán',
    icon: QrCodeIcon
  }];
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  return <>
      <nav className="sticky top-0 z-40 bg-[#0D0D0D]/95 backdrop-blur-md border-b border-[#2A2A2A]" data-id="element-152">
        <div className="max-w-7xl mx-auto px-4" data-id="element-153">
          <div className="flex items-center justify-between h-16" data-id="element-154">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-id="element-155">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C8A84E] to-[#B8963E] flex items-center justify-center" data-id="element-156">
                <span className="text-[#0D0D0D] font-bold text-xs" data-id="element-157">MHP</span>
              </div>
              <div className="hidden sm:block" data-id="element-158">
                <span className="text-white font-semibold text-sm" data-id="element-159">
                  Mỹ Hòa Phát
                </span>
                <span className="block text-[#C8A84E] text-[10px] font-medium tracking-wider uppercase" data-id="element-160">
                  Khách hàng thân thiết
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1" data-id="element-161">
              {navLinks.map(link => {
              const Icon = link.icon;
              const active = isActive(link.to);
              return <Link key={link.to} to={link.to} className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${active ? 'text-[#C8A84E]' : 'text-[#B0B0B0] hover:text-white'}`} data-id="element-162">
                    <Icon className="w-4 h-4" data-id="element-163" />
                    {link.label}
                    {active && <motion.div layoutId="customerNavIndicator" className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#C8A84E] rounded-full" transition={{
                  type: 'spring',
                  bounce: 0.15,
                  duration: 0.4
                }} data-id="element-164" />}
                  </Link>;
            })}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3" data-id="element-165">
              {isCustomerLoggedIn && currentCustomer ? <div className="hidden md:flex items-center gap-3" data-id="element-166">
                  <Badge variant="tier" tier={currentCustomer.tier} size="md" data-id="element-167">
                    {getTierNameVi(currentCustomer.tier)}
                  </Badge>
                  <Link to="/ho-so" className="flex items-center gap-2 text-sm text-[#B0B0B0] hover:text-white transition-colors" data-id="element-168">
                    <div className="w-8 h-8 rounded-full bg-[#2A2A2A] flex items-center justify-center" data-id="element-169">
                      <UserIcon className="w-4 h-4" data-id="element-170" />
                    </div>
                    <span className="max-w-[120px] truncate" data-id="element-171">
                      {currentCustomer.name || currentCustomer.phone}
                    </span>
                  </Link>
                  <Link to="/admin" className="p-2 text-[#666666] hover:text-[#C8A84E] transition-colors rounded-lg" title="Admin Portal" data-id="element-172">
                    <SettingsIcon className="w-4 h-4" data-id="element-173" />
                  </Link>
                  <button onClick={customerLogout} className="p-2 text-[#666666] hover:text-[#EF4444] transition-colors rounded-lg" aria-label="Đăng xuất" data-id="element-174">
                    <LogOutIcon className="w-4 h-4" data-id="element-175" />
                  </button>
                </div> : <div className="hidden md:flex items-center gap-2" data-id="element-176">
                  <Link to="/admin" className="p-2 text-[#666666] hover:text-[#C8A84E] transition-colors rounded-lg" title="Admin Portal" data-id="element-177">
                    <SettingsIcon className="w-4 h-4" data-id="element-178" />
                  </Link>
                  <Link to="/dang-nhap" className="inline-flex items-center gap-2 px-4 py-2 bg-[#C8A84E] text-[#0D0D0D] text-sm font-semibold rounded-lg hover:bg-[#B8963E] transition-colors" data-id="element-179">
                    Đăng Nhập
                  </Link>
                </div>}

              {/* Mobile menu button */}
              <button onClick={() => setIsMobileOpen(!isMobileOpen)} className="lg:hidden p-2 text-[#B0B0B0] hover:text-white transition-colors" aria-label="Menu" data-id="element-180">
                {isMobileOpen ? <XIcon className="w-5 h-5" data-id="element-181" /> : <MenuIcon className="w-5 h-5" data-id="element-182" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-183">
        {isMobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden fixed top-16 left-0 right-0 z-30 bg-[#0D0D0D] border-b border-[#2A2A2A] overflow-hidden" data-id="element-184">
            <div className="px-4 py-3 space-y-1" data-id="element-185">
              {navLinks.map(link => {
            const Icon = link.icon;
            const active = isActive(link.to);
            return <Link key={link.to} to={link.to} onClick={() => setIsMobileOpen(false)} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-[#C8A84E]/10 text-[#C8A84E]' : 'text-[#B0B0B0] hover:bg-[#1A1A1A] hover:text-white'}`} data-id="element-186">
                    <Icon className="w-4 h-4" data-id="element-187" />
                    {link.label}
                  </Link>;
          })}
              <div className="pt-2 border-t border-[#2A2A2A]" data-id="element-188">
                <Link to="/admin" onClick={() => setIsMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#666666] hover:bg-[#1A1A1A] hover:text-[#C8A84E]" data-id="element-189">
                  <SettingsIcon className="w-4 h-4" data-id="element-190" />
                  Admin Portal
                </Link>
                {isCustomerLoggedIn ? <>
                    <Link to="/ho-so" onClick={() => setIsMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#B0B0B0] hover:bg-[#1A1A1A] hover:text-white" data-id="element-191">
                      <UserIcon className="w-4 h-4" data-id="element-192" />
                      Hồ Sơ
                    </Link>
                    <button onClick={() => {
                customerLogout();
                setIsMobileOpen(false);
              }} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#EF4444] hover:bg-[#EF4444]/10 w-full text-left" data-id="element-193">
                      <LogOutIcon className="w-4 h-4" data-id="element-194" />
                      Đăng Xuất
                    </button>
                  </> : <Link to="/dang-nhap" onClick={() => setIsMobileOpen(false)} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C8A84E] text-[#0D0D0D] text-sm font-semibold rounded-lg" data-id="element-195">
                    Đăng Nhập
                  </Link>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}